import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  GoogleMap,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { useGoogleMaps } from "./useGoogleMaps";
import { Search, MapPin, Navigation, Star, Clock, Phone, ExternalLink } from "lucide-react";
import "./dashboard.css";

// Popular search categories for quick access
const popularCategories = [
  { label: "Petrol Pump", query: "petrol pump", icon: "⛽" },
  { label: "Restaurant", query: "restaurant", icon: "🍽️" },
  { label: "Hospital", query: "hospital", icon: "🏥" },
  { label: "ATM", query: "atm", icon: "🏧" },
  { label: "Pharmacy", query: "pharmacy", icon: "💊" },
  { label: "Hotel", query: "hotel", icon: "🏨" },
];

const Nearby = () => {
  const { isLoaded: isMapsLoaded } = useGoogleMaps();

  const [mapCenter, setMapCenter] = useState({ lat: 20.5937, lng: 78.9629 });
  const [userLocation, setUserLocation] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [places, setPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapRef, setMapRef] = useState(null);
  const [isInfoWindowOpen, setIsInfoWindowOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchInputRef = useRef(null);

  // Get user's current location on component mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({ lat: latitude, lng: longitude });
          setMapCenter({ lat: latitude, lng: longitude });
          setZoomLevel(14);
        },
        (error) => {
          console.error("Error getting location:", error);
          // Default to India center if location access denied
          setMapCenter({ lat: 20.5937, lng: 78.9629 });
        }
      );
    }
  }, []);

  // Search for nearby places
  const searchNearbyPlaces = useCallback((query) => {
    if (!mapRef || !query.trim()) return;

    setIsLoading(true);
    setError(null);
    setHasSearched(true);

    const service = new window.google.maps.places.PlacesService(mapRef);
    const searchCenter = userLocation || mapCenter;

    const request = {
      location: new window.google.maps.LatLng(searchCenter.lat, searchCenter.lng),
      radius: 5000, // 5km radius
      keyword: query,
    };

    service.nearbySearch(request, (results, status) => {
      setIsLoading(false);
      if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
        setPlaces(results);
        if (results.length > 0) {
          // Center map to show all results
          const bounds = new window.google.maps.LatLngBounds();
          results.forEach((place) => {
            bounds.extend(place.geometry.location);
          });
          mapRef.fitBounds(bounds);
        }
      } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
        setPlaces([]);
        setError("No places found nearby. Try a different search or location.");
      } else {
        setError("Error searching for places. Please try again.");
      }
    });
  }, [mapRef, userLocation, mapCenter]);

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      searchNearbyPlaces(searchQuery);
    }
  };

  // Handle category quick search
  const handleCategorySearch = (query) => {
    setSearchQuery(query);
    searchNearbyPlaces(query);
  };

  // Handle place click from list
  const handlePlaceClick = (place) => {
    setSelectedPlace(place);
    setIsInfoWindowOpen(true);
    setMapCenter({
      lat: place.geometry.location.lat(),
      lng: place.geometry.location.lng(),
    });
    setZoomLevel(16);
  };

  // Handle marker click on map
  const handleMarkerClick = (place) => {
    setSelectedPlace(place);
    setIsInfoWindowOpen(true);
  };

  // Get directions to place
  const getDirections = (place) => {
    const destination = `${place.geometry.location.lat()},${place.geometry.location.lng()}`;
    const origin = userLocation ? `${userLocation.lat},${userLocation.lng}` : "";
    const url = origin
      ? `https://www.google.com/maps/dir/${origin}/${destination}`
      : `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    window.open(url, "_blank");
  };

  // Format rating stars
  const renderRating = (rating) => {
    if (!rating) return null;
    return (
      <div className="d-flex align-items-center">
        <Star size={12} className="me-1" style={{ color: "#FFB800", fill: "#FFB800" }} />
        <span style={{ fontSize: "12px", color: "#666" }}>{rating.toFixed(1)}</span>
      </div>
    );
  };

  // Get open status
  const getOpenStatus = (place) => {
    if (!place.opening_hours) return null;
    return place.opening_hours.isOpen?.() ? (
      <span style={{ color: "#22c55e", fontSize: "11px", fontWeight: "600" }}>Open</span>
    ) : (
      <span style={{ color: "#ef4444", fontSize: "11px", fontWeight: "600" }}>Closed</span>
    );
  };

  return (
    <div className="container-fluid m-0 p-0">
      <div className="row">
        {/* Header with Search */}
        <div
          className="col-12 p-3 border-bottom"
          style={{ backgroundColor: "#EFF0F4", zIndex: 1000 }}
        >
          <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3">
            <h5 className="mb-0 fw-bold" style={{ color: "#333", whiteSpace: "nowrap" }}>
              <MapPin size={20} className="me-2" style={{ color: "#fd610a" }} />
              Nearby Places
            </h5>

            <form onSubmit={handleSearch} className="flex-grow-1 w-100">
              <div className="d-flex align-items-center gap-2">
                <div className="position-relative flex-grow-1" style={{ maxWidth: "500px" }}>
                  <Search
                    size={18}
                    className="position-absolute"
                    style={{ left: "12px", top: "50%", transform: "translateY(-50%)", color: "#999" }}
                  />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search nearby places (e.g., Petrol Pump, Restaurant, ATM...)"
                    className="form-control ps-5"
                    style={{
                      borderRadius: "25px",
                      border: "1px solid #ddd",
                      padding: "10px 16px 10px 40px",
                      fontSize: "14px",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                    }}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  className="btn"
                  style={{
                    backgroundColor: "#fd610a",
                    color: "white",
                    borderRadius: "25px",
                    padding: "10px 24px",
                    fontWeight: "600",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  }}
                  disabled={isLoading || !searchQuery.trim()}
                >
                  {isLoading ? "Searching..." : "Search"}
                </button>
              </div>
            </form>
          </div>

          {/* Popular Categories */}
          <div className="mt-3 d-flex flex-wrap gap-2">
            {popularCategories.map((cat, index) => (
              <button
                key={index}
                className="btn btn-sm"
                style={{
                  backgroundColor: searchQuery === cat.query ? "#fd610a" : "#fff",
                  color: searchQuery === cat.query ? "#fff" : "#333",
                  border: "1px solid #ddd",
                  borderRadius: "20px",
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: "1000",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                onClick={() => handleCategorySearch(cat.query)}
              >
                <span>{cat.icon}</span>
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="col-12">
          <div className="row">
            {/* Places List - Left Side */}
            <div
              className="col-12 col-md-4 col-lg-3 p-0"
              style={{
                backgroundColor: "#fff",
                borderRight: "1px solid #eee",
                height: "calc(100vh - 220px)",
                overflowY: "auto",
              }}
            >
              {/* Results Header */}
              <div
                className="p-3 border-bottom sticky-top bg-white"
                style={{ zIndex: 10 }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-bold" style={{ color: "#333", fontSize: "14px" }}>
                    {hasSearched
                      ? `${places.length} Places Found`
                      : "Search for nearby places"}
                  </span>
                  {userLocation && (
                    <button
                      className="btn btn-sm"
                      style={{
                        backgroundColor: "#f0f0f0",
                        borderRadius: "15px",
                        fontSize: "11px",
                        padding: "4px 10px",
                      }}
                      onClick={() => {
                        setMapCenter(userLocation);
                        setZoomLevel(14);
                      }}
                    >
                      <Navigation size={12} className="me-1" />
                      My Location
                    </button>
                  )}
                </div>
              </div>

              {/* Loading State */}
              {isLoading && (
                <div className="text-center py-5">
                  <div className="spinner-border text-warning" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2 text-muted" style={{ fontSize: "14px" }}>
                    Searching nearby places...
                  </p>
                </div>
              )}

              {/* Error State */}
              {error && !isLoading && (
                <div className="text-center py-5 px-3">
                  <MapPin size={48} className="mb-3" style={{ color: "#ddd" }} />
                  <p className="text-muted" style={{ fontSize: "14px" }}>
                    {error}
                  </p>
                </div>
              )}

              {/* Empty State */}
              {!hasSearched && !isLoading && (
                <div className="text-center py-5 px-3">
                  <Search size={48} className="mb-3" style={{ color: "#ddd" }} />
                  <p className="text-muted" style={{ fontSize: "14px" }}>
                    Search for places like petrol pumps, restaurants, ATMs, hospitals, and more.
                  </p>
                  <p className="text-muted" style={{ fontSize: "12px" }}>
                    Or click on a category above to get started.
                  </p>
                </div>
              )}

              {/* No Results */}
              {hasSearched && places.length === 0 && !isLoading && !error && (
                <div className="text-center py-5 px-3">
                  <MapPin size={48} className="mb-3" style={{ color: "#ddd" }} />
                  <p className="text-muted" style={{ fontSize: "14px" }}>
                    No places found for "{searchQuery}"
                  </p>
                  <p className="text-muted" style={{ fontSize: "12px" }}>
                    Try a different search term or location.
                  </p>
                </div>
              )}

              {/* Places List */}
              {!isLoading && places.length > 0 && (
                <div className="places-list">
                  {places.map((place, index) => (
                    <div
                      key={place.place_id}
                      className="p-3 border-bottom"
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedPlace?.place_id === place.place_id
                            ? "#fff5f0"
                            : "#fff",
                        borderLeft:
                          selectedPlace?.place_id === place.place_id
                            ? "3px solid #fd610a"
                            : "3px solid transparent",
                        transition: "all 0.2s ease",
                      }}
                      onClick={() => handlePlaceClick(place)}
                      onMouseEnter={(e) => {
                        if (selectedPlace?.place_id !== place.place_id) {
                          e.currentTarget.style.backgroundColor = "#fafafa";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedPlace?.place_id !== place.place_id) {
                          e.currentTarget.style.backgroundColor = "#fff";
                        }
                      }}
                    >
                      <div className="d-flex align-items-start">
                        {/* Place Photo or Icon */}
                        <div
                          className="me-3 d-flex align-items-center justify-content-center"
                          style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "10px",
                            backgroundColor: "#f5f5f5",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          {place.photos && place.photos[0] ? (
                            <img
                              src={place.photos[0].getUrl({ maxWidth: 100, maxHeight: 100 })}
                              alt={place.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <MapPin size={24} style={{ color: "#fd610a" }} />
                          )}
                        </div>

                        {/* Place Details */}
                        <div className="flex-grow-1 min-width-0">
                          <h6
                            className="mb-1 text-truncate"
                            style={{
                              fontSize: "14px",
                              fontWeight: "600",
                              color:
                                selectedPlace?.place_id === place.place_id
                                  ? "#fd610a"
                                  : "#333",
                            }}
                          >
                            {place.name}
                          </h6>
                          <p
                            className="mb-1 text-truncate"
                            style={{ fontSize: "12px", color: "#666" }}
                          >
                            {place.vicinity}
                          </p>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            {renderRating(place.rating)}
                            {getOpenStatus(place)}
                          </div>
                        </div>

                        {/* Direction Button */}
                        <button
                          className="btn btn-sm ms-2"
                          style={{
                            backgroundColor: "#fd610a",
                            color: "white",
                            borderRadius: "50%",
                            width: "32px",
                            height: "32px",
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            getDirections(place);
                          }}
                          title="Get Directions"
                        >
                          <Navigation size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Map - Right Side */}
            <div
              className="col-12 col-md-8 col-lg-9 p-0"
              style={{ height: "calc(100vh - 220px)" }}
            >
              {!isMapsLoaded ? (
                <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#888" }}>
                  Loading map…
                </div>
              ) : (
                <GoogleMap
                  mapContainerStyle={{ height: "100%", width: "100%" }}
                  center={mapCenter}
                  zoom={zoomLevel}
                  onLoad={(map) => setMapRef(map)}
                  options={{
                    zoomControl: true,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: true,
                  }}
                >
                  {/* User Location Marker */}
                  {userLocation && (
                    <Marker
                      position={userLocation}
                      icon={{
                        path: window.google?.maps?.SymbolPath?.CIRCLE,
                        scale: 8,
                        fillColor: "#4285F4",
                        fillOpacity: 1,
                        strokeColor: "#fff",
                        strokeWeight: 2,
                      }}
                      title="Your Location"
                    />
                  )}

                  {/* Place Markers */}
                  {places.map((place) => (
                    <Marker
                      key={place.place_id}
                      position={{
                        lat: place.geometry.location.lat(),
                        lng: place.geometry.location.lng(),
                      }}
                      onClick={() => handleMarkerClick(place)}
                      icon={{
                        url:
                          selectedPlace?.place_id === place.place_id
                            ? "http://maps.google.com/mapfiles/ms/icons/orange-dot.png"
                            : "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                      }}
                    />
                  ))}

                  {/* Info Window for Selected Place */}
                  {selectedPlace && isInfoWindowOpen && (
                    <InfoWindow
                      position={{
                        lat: selectedPlace.geometry.location.lat(),
                        lng: selectedPlace.geometry.location.lng(),
                      }}
                      onCloseClick={() => {
                        setIsInfoWindowOpen(false);
                        setSelectedPlace(null);
                      }}
                    >
                      <div style={{ maxWidth: "250px", padding: "5px" }}>
                        <h6
                          style={{
                            margin: "0 0 8px 0",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#fd610a",
                          }}
                        >
                          {selectedPlace.name}
                        </h6>
                        <p
                          style={{
                            margin: "0 0 8px 0",
                            fontSize: "12px",
                            color: "#666",
                          }}
                        >
                          {selectedPlace.vicinity}
                        </p>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          {renderRating(selectedPlace.rating)}
                          {getOpenStatus(selectedPlace)}
                        </div>
                        <button
                          className="btn btn-sm w-100"
                          style={{
                            backgroundColor: "#fd610a",
                            color: "white",
                            borderRadius: "20px",
                            fontSize: "12px",
                            padding: "6px 12px",
                          }}
                          onClick={() => getDirections(selectedPlace)}
                        >
                          <Navigation size={12} className="me-1" />
                          Get Directions
                        </button>
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile responsive styles */}
      <style>{`
        @media (max-width: 767px) {
          .places-list {
            max-height: 40vh;
          }
        }

        .places-list::-webkit-scrollbar {
          width: 6px;
        }

        .places-list::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .places-list::-webkit-scrollbar-thumb {
          background: #ccc;
          border-radius: 3px;
        }

        .places-list::-webkit-scrollbar-thumb:hover {
          background: #999;
        }
      `}</style>
    </div>
  );
};

export default Nearby;
